import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ClipboardCheck, ArrowLeft, Search, Plus, Clock, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BedCountSheet } from './BedCountSheet';
import { BedCountPrintView } from './BedCountPrintView';
import { AvcReportCard } from './AvcReportCard';
import { DailyWaitlistPanel } from './DailyWaitlistPanel';
import { logActivity } from '@/hooks/useActivityLog';
import type { BedCount } from '@/hooks/useBedCount';

interface BedCountLauncherProps {
  siteId: string;
  siteName?: string;
  canSignOff?: boolean;
  /** Optional: render as a small inline launcher button (for use in card headers) */
  variant?: 'inline' | 'panel';
}

const statusConfig: Record<string, { label: string; className: string; icon: any }> = {
  pending: { label: 'Pending', className: 'bg-muted text-muted-foreground', icon: Clock },
  in_progress: { label: 'In Progress', className: 'bg-primary/15 text-primary', icon: ClipboardCheck },
  awaiting_signoff: { label: 'Awaiting Sign-Off', className: 'bg-amber-500/15 text-amber-700', icon: Clock },
  completed: { label: 'Completed', className: 'bg-success/15 text-success', icon: CheckCircle2 },
};

export const BedCountLauncher = ({
  siteId,
  siteName,
  canSignOff = false,
  variant = 'panel',
}: BedCountLauncherProps) => {
  const qc = useQueryClient();
  const [activeBedCountId, setActiveBedCountId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  // Fetch all bed counts for this site (history + active)
  const { data: bedCounts = [] } = useQuery({
    queryKey: ['bed-counts', 'history', siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bed_counts')
        .select('*')
        .eq('site_id', siteId)
        .order('scheduled_for', { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as BedCount[];
    },
  });

  // Realtime invalidation
  useEffect(() => {
    if (!siteId) return;
    const channel = supabase
      .channel(`bed-counts-history-${siteId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bed_counts', filter: `site_id=eq.${siteId}` }, () => {
        qc.invalidateQueries({ queryKey: ['bed-counts', 'history', siteId] });
        qc.invalidateQueries({ queryKey: ['bed-count'] });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bed_count_entries' }, () => {
        qc.invalidateQueries({ queryKey: ['bed-count', 'entries'] });
        qc.invalidateQueries({ queryKey: ['bed-counts', 'history', siteId] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [siteId, qc]);

  const activeBedCount = useMemo(
    () => bedCounts.find((b) => b.id === activeBedCountId) ?? null,
    [bedCounts, activeBedCountId]
  );

  const filteredHistory = useMemo(() => {
    if (!search.trim()) return bedCounts;
    const q = search.toLowerCase();
    return bedCounts.filter((b) => {
      const date = format(new Date(b.scheduled_for), 'PPP p').toLowerCase();
      return date.includes(q) || b.status.toLowerCase().includes(q);
    });
  }, [bedCounts, search]);

  const handleStartNew = async () => {
    if (!siteId) {
      toast.error('Select a facility first');
      return;
    }
    setCreating(true);
    try {
      const { data, error } = await supabase
        .from('bed_counts')
        .insert({
          site_id: siteId,
          scheduled_for: new Date().toISOString(),
          status: 'pending',
        })
        .select()
        .single();
      if (error) throw error;

      await logActivity({
        actionType: 'bed_count' as any,
        actionDetails: { event: 'bed_count_created', bed_count_id: data.id },
        siteId,
      });

      qc.invalidateQueries({ queryKey: ['bed-counts', 'history', siteId] });
      setActiveBedCountId(data.id);
      toast.success('Bed count created — ready to start');
    } catch (e: any) {
      toast.error(e.message || 'Failed to create bed count');
    } finally {
      setCreating(false);
    }
  };

  // Inline trigger button only (no panel) — for use in headers
  if (variant === 'inline' && !activeBedCount) {
    const inProgress = bedCounts.find((b) => b.status === 'in_progress' || b.status === 'awaiting_signoff' || b.status === 'pending');
    return (
      <Button
        variant="outline"
        onClick={() => {
          if (inProgress) setActiveBedCountId(inProgress.id);
          else handleStartNew();
        }}
        disabled={creating}
        className="gap-2 h-10 px-4 text-base font-medium"
      >
        <ClipboardCheck className="h-5 w-5" />
        {inProgress ? 'Resume Bed Count' : 'Bed Count'}
      </Button>
    );
  }

  // Active sheet view
  if (activeBedCount) {
    const showAvc = activeBedCount.status === 'completed';
    return (
      <div className="space-y-3">
        <div className="bed-count-screen-only space-y-3">
          <Button variant="ghost" size="sm" onClick={() => setActiveBedCountId(null)} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Bed Count History
          </Button>
          <BedCountSheet
            bedCount={activeBedCount}
            siteId={siteId}
            siteName={siteName}
            canSignOff={canSignOff}
            onPrint={() => window.print()}
          />
          {showAvc && (
            <>
              <AvcReportCard bedCount={activeBedCount} siteId={siteId} />
              <DailyWaitlistPanel siteId={siteId} />
            </>
          )}
        </div>
        {/* Print-only view rendered hidden on screen, shown only during print */}
        <div className="bed-count-print-only">
          <BedCountPrintView bedCount={activeBedCount} siteName={siteName} />
        </div>
      </div>
    );
  }

  // Panel view: history + start button
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" /> Bed Counts
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Pulled from the current roster at {siteName ?? 'this facility'}.
            </p>
          </div>
          <Button onClick={handleStartNew} disabled={creating || !siteId} className="gap-2">
            <Plus className="h-4 w-4" />
            New Bed Count
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by date or status..."
            className="pl-9"
          />
        </div>

        {filteredHistory.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            {bedCounts.length === 0
              ? 'No bed counts yet. Click "New Bed Count" to start the first one.'
              : 'No bed counts match your search.'}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredHistory.map((b) => {
              const cfg = statusConfig[b.status] ?? statusConfig.pending;
              const Icon = cfg.icon;
              return (
                <button
                  key={b.id}
                  onClick={() => setActiveBedCountId(b.id)}
                  className="w-full text-left border rounded-lg p-3 hover:bg-muted/40 transition-colors flex items-center justify-between gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">
                      {format(new Date(b.scheduled_for), 'EEEE, MMM d, yyyy')}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {format(new Date(b.scheduled_for), 'h:mm a')}
                      {b.signed_off_at && ` · Signed off ${format(new Date(b.signed_off_at), 'h:mm a')}`}
                    </div>
                  </div>
                  <Badge className={cfg.className} variant="secondary">
                    <Icon className="h-3 w-3 mr-1" />
                    {cfg.label}
                  </Badge>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
